import type { ComponentType, ReactNode } from 'react'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'
import { CaretDownIcon } from '@phosphor-icons/react'

interface SidebarNavGroupProps {
  label: string
  icon: ComponentType<{ className?: string }>
  containsActive: boolean
  storedCollapsed?: boolean
  unreadCount?: number
  className?: string
  onCollapsedChange: (collapsed: boolean) => void
  children: ReactNode
}

export function SidebarNavGroup({
  label,
  icon: Icon,
  containsActive,
  storedCollapsed,
  unreadCount,
  className,
  onCollapsedChange,
  children,
}: SidebarNavGroupProps) {
  // Derive the first-visit default without writing a preference. A saved
  // choice wins, including when someone closes the area they are viewing.
  const isCollapsed = storedCollapsed ?? !containsActive

  return (
    <Collapsible
      open={!isCollapsed}
      onOpenChange={open => onCollapsedChange(!open)}
    >
      <CollapsibleTrigger asChild>
        <button
          type="button"
          data-active={containsActive && isCollapsed}
          className={cn(
            'focus-visible:ring-sidebar-ring relative flex h-8 w-full items-center gap-2 rounded-lg px-2.5 text-[13px] font-[450] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2',
            containsActive && 'font-[550]',
            className,
          )}
        >
          {containsActive && isCollapsed && (
            <span
              aria-hidden
              className="absolute bottom-2 left-0.5 top-2 w-0.5 rounded-full bg-current"
            />
          )}
          <span
            aria-hidden
            className="flex h-4 w-4 shrink-0 items-center justify-center"
          >
            <Icon className="h-4 w-4" />
          </span>
          <span className="flex-1 truncate text-left">{label}</span>
          {containsActive && <span className="sr-only">Current area</span>}
          {isCollapsed &&
            typeof unreadCount === 'number' &&
            unreadCount > 0 && (
              <span className="min-w-[18px] rounded-full bg-primary px-1.5 py-0.5 text-center text-[10px] font-medium text-primary-foreground">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          <CaretDownIcon
            weight="regular"
            aria-hidden
            className={cn(
              'h-3 w-3 shrink-0 opacity-60 transition-transform duration-150 motion-reduce:transition-none',
              isCollapsed && '-rotate-90',
            )}
          />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="pl-5 pt-0.5">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  )
}
